import React, { FunctionComponent } from 'react';
import { ViewBillDetails } from '../../../__generated__/pge-types';
import PGEButton from '../../buttons';
import { ExpandMore, PictureAsPdf } from '@material-ui/icons';
import { Menu, MenuItem } from '@material-ui/core';
import colors from '../../../themes/main-colors';
import { useTranslation } from '../../../hooks/useTranslation';
import useWrapWithLoader from '../../../hooks/useWrapWithLoading';
import useDownloadBill from '../../../hooks/useDownloadBill';
import { toDateString } from '../../../util/format';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';

interface DownloadBillButtonProps {
  billDetails: ViewBillDetails | null;
  isSummaryBillAccount?: boolean | undefined;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    outlineButton: {
      padding: '8px 10px',
      width: '100%',
      justifyContent: 'space-between',
      '&:hover': {
        backgroundColor: 'transparent',
        borderColor: colors.noirBlur,
        color: colors.noirBlur,
      },
    },
  }),
);

const DownloadBillButton: FunctionComponent<DownloadBillButtonProps> = ({
  billDetails,
  isSummaryBillAccount,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const { wrapWithLoader } = useWrapWithLoader();
  const downloader = useDownloadBill();

  const downloadBill = wrapWithLoader(
    async (e: React.MouseEvent, isSummary: boolean, isNonDetailed: boolean) => {
      if (e) {
        e.preventDefault();
        setAnchorEl(null);
      }

      const downloadLink = document.createElement('a');

      downloadLink.href = await downloader.downloadBill(
        billDetails?.encryptedBillId,
        isSummary,
        isNonDetailed,
      );

      downloadLink.download = `${t('VIEW_BILL').replace(
        ' ',
        '_',
      )}-${toDateString(billDetails?.billingPeriodStartDate)}_${toDateString(
        billDetails?.billingPeriodEndDate,
      )}.pdf`;

      downloadLink.click();
    },
    () => t('NO_BILL_FOUND'),
  );

  return (
    <>
      <PGEButton
        size={'large'}
        variant="outlined"
        endIcon={<ExpandMore />}
        onClick={handleClick}
        data-testid="positioned-button"
        id="positioned-button"
        aria-controls={open ? 'positioned-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        className={classes.outlineButton}
      >
        {t('DOWNLOAD_PDF')}
      </PGEButton>
      <Menu
        id="positioned-menu"
        aria-labelledby="positioned-button"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          style: {
            background: colors.brightBlue,
            color: colors.white,
            padding: '10px',
          },
        }}
      >
        {isSummaryBillAccount ? (
          <>
            <MenuItem onClick={e => downloadBill(e, false, false)}>
              <PictureAsPdf style={{ marginRight: '10px' }} />
              {t('DETAILED_BILL_DOWNLOAD')}
            </MenuItem>
            <MenuItem onClick={e => downloadBill(e, true, false)}>
              <PictureAsPdf style={{ marginRight: '10px' }} />
              {t('SUMMARY_BILL_DOWNLOAD')}
            </MenuItem>
          </>
        ) : (
          <>
            <MenuItem onClick={e => downloadBill(e, false, true)}>
              <PictureAsPdf style={{ marginRight: '10px' }} />
              {t('DOWNLOAD_SIMPLIFIED_BILL')}
            </MenuItem>
            <MenuItem onClick={e => downloadBill(e, false, false)}>
              <PictureAsPdf style={{ marginRight: '10px' }} />
              {t('DETAILED_BILL_DOWNLOAD')}
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
};

export default DownloadBillButton;
